import { useMemo } from 'react';
import { toast } from 'react-toastify';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@ds';
import { Form } from '@ui';
import { FormActions } from '@features/forms/FormActions/FormActions';
import { useDeviceTrackingDisplaySettingsMutation } from '@/queries/device-tracking';
import { RouteDeviceTracking } from '@/api/apis/route';
import { retrieveUnifiedError } from '@/api/utils';
import { EditLocationTrackingForm } from './EditLocationTrackingForm';
import { getLocationTrackingInitialValues, locationTrackingFormSchema, LocationTrackingFormValues } from './config';

const MODAL_CLASS_NAMES = {
  wrapper: 'flex-wrap',
  base: 'p-6 bg-base-80',
  header: 'flex justify-between p-0 pb-4 border-b-1 border-base-30',
  body: 'p-0 my-6',
  footer: 'p-0 pt-6 border-t-1 border-base-30 justify-between',
} as const;

type EditLocationTrackingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  deviceTracking: RouteDeviceTracking;
};

export const EditLocationTrackingModal = ({ isOpen, onClose, deviceTracking }: EditLocationTrackingModalProps) => {
  const { mutateAsync, isPending } = useDeviceTrackingDisplaySettingsMutation();

  const handleSubmit = async (formData: LocationTrackingFormValues) => {
    try {
      await mutateAsync({
        deviceTrackingId: deviceTracking.id,
        caseId: deviceTracking.caseId,
        data: {
          version: deviceTracking.version,
          displayEstimatedTimeOfArrival: formData.displayEstimatedTimeOfArrival,
          displayLocationTracking: formData.displayLocationTracking,
          deviceToDisplay: formData.deviceToDisplay || null,
        },
      });
      toast.success('Location Tracking settings saved successfully.');
      onClose();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const formActions = useMemo(
    () => ({
      size: 'lg' as const,
      discard: {
        label: 'Discard',
        variant: 'light' as const,
        handler: onClose,
      },
      submit: {
        label: 'Save Changes',
        isSubmitting: isPending,
      },
    }),
    [onClose, isPending],
  );

  return (
    <Modal
      classNames={MODAL_CLASS_NAMES}
      size="4xl"
      backdrop="opaque"
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton
    >
      <ModalContent>
        <Form<LocationTrackingFormValues>
          defaultValues={getLocationTrackingInitialValues(deviceTracking.displaySettings)}
          schema={locationTrackingFormSchema}
          onSubmit={handleSubmit}
        >
          <ModalHeader>
            <h2>Edit Location Tracking</h2>
          </ModalHeader>
          <ModalBody>
            <EditLocationTrackingForm graphs={deviceTracking.graphs} />
          </ModalBody>
          <ModalFooter>
            <FormActions actions={formActions} />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
